import { supabase } from '@/integrations/supabase/client';
import {
  oddpoolArbitrage,
  oddpoolFeedCatalog,
  oddpoolHistoricalKalshi,
  oddpoolHistoricalPolymarket,
  oddpoolOhlcv,
  oddpoolSearch,
  oddpoolWhales,
} from '@/lib/oddpool.functions';
import {
  aiArbitrageExplainer,
  aiDailyDigest,
  aiMarketAnalyst,
  aiResearchCopilot,
  aiWhaleInterpreter,
} from '@/lib/predictionAi.functions';

/** Upstream exposes several exchanges; keep it open-ended. */
export type Venue = string;

export interface OddpoolMarket {
  market_id: string;
  event_id?: string;
  venue: Venue;
  title?: string;
  question?: string;
  outcome?: string;
  price?: number;
  yes_price?: number;
  no_price?: number;
  volume?: number;
  liquidity?: number;
  end_date?: string;
  settled_at?: string;
  series_id?: string;
  /** Polymarket CLOB outcome token (needed to sign an order for this leg). */
  yes_token_id?: string;
  no_token_id?: string;
}


export interface OddpoolEvent {
  event_id: string;
  venue: Venue;
  title: string;
  series_id?: string;
  end_date?: string;
  volume?: number;
  liquidity?: number;
  market_count?: number;
  category?: string;
  status?: string;
  image_url?: string;
  market_questions?: string[];
  discovered_at?: string;
  outcomes?: Array<{ name: string; price?: number; market_id?: string }>;
}

export interface ArbOpportunity {
  event_id?: string;
  outcome?: string;
  venue_a?: Venue;
  venue_b?: Venue;
  price_a?: number;
  price_b?: number;
  net_cents?: number;
  yield_to_resolution?: number;
  question?: string;
  title?: string;
}

export interface WhaleTrade {
  trade_id?: string;
  event_id?: string;
  market_id?: string;
  venue?: Venue;
  side?: 'buy' | 'sell';
  outcome?: string;
  size?: number;
  price?: number;
  notional?: number;
  ts?: string;
  trader?: string;
}

export interface OhlcvBar {
  ts: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface AiAnalysis {
  recommendation: 'yes' | 'no' | 'neutral';
  confidence: number;
  summary: string;
  bull_case: string[];
  bear_case: string[];
  key_risks: string[];
  suggested_outcome_market_id?: string | null;
}

/** Thrown when the upstream plan does not cover an endpoint. */
export class OddpoolPlanError extends Error {}

type Row = Record<string, unknown>;

function describe(payload: unknown): never {
  const d = (payload ?? {}) as { error?: string; status?: number; data?: { detail?: string } };
  const detail = d.data?.detail || '';
  if (d.status === 403 || /plan does not include/i.test(detail)) {
    throw new OddpoolPlanError(detail || 'This data set is not included in the current data plan.');
  }
  if (d.status === 429 || /rate limit/i.test(detail)) {
    throw new Error('Rate limited by the data provider, please try again in a moment.');
  }
  throw new Error(detail || d.error || 'Market data error');
}

/**
 * Market data and the AI agents now run inside the app. The websocket relay is
 * the one exception: it holds a long-lived upstream connection, so it stays on
 * the Supabase side.
 */
const handlers: Record<string, (body: Record<string, unknown>) => Promise<unknown>> = {
  'oddpool-search': (body) => oddpoolSearch({ data: body }),
  'oddpool-arbitrage': (body) => oddpoolArbitrage({ data: body }),
  'oddpool-whales': (body) => oddpoolWhales({ data: body }),
  'oddpool-ohlcv': (body) => oddpoolOhlcv({ data: body }),
  'oddpool-historical-kalshi': (body) => oddpoolHistoricalKalshi({ data: body }),
  'oddpool-historical-polymarket': (body) => oddpoolHistoricalPolymarket({ data: body }),
  'oddpool-feed-catalog': (body) => oddpoolFeedCatalog({ data: body }),
  'ai-market-analyst': (body) =>
    aiMarketAnalyst({ data: body as { event_id: string; title?: string } }),
  'ai-arbitrage-explainer': (body) =>
    aiArbitrageExplainer({ data: body as { opportunity: Record<string, unknown> } }),
  'ai-whale-interpreter': (body) => aiWhaleInterpreter({ data: body }),
  'ai-research-copilot': (body) =>
    aiResearchCopilot({ data: body as { messages: Array<{ role: string; content: string }> } }),
  'ai-daily-digest': () => aiDailyDigest(),
};

async function invoke<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const handler = handlers[name];

  if (handler) {
    let data: unknown;
    try {
      data = await handler(body);
    } catch (e) {
      throw new Error((e as Error).message || 'Market data error');
    }
    if (data && typeof data === 'object' && 'error' in data) describe(data);
    return data as T;
  }

  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    // Non-2xx responses surface as FunctionsHttpError with the body on `context`.
    const res = (error as { context?: Response }).context;
    if (res && typeof res.json === 'function') {
      const payload = await res.json().catch(() => null);
      if (payload) describe(payload);
    }
    throw error;
  }

  if (data && typeof data === 'object' && 'error' in data) describe(data);

  return data as T;
}

/* ------------------------------------------------------------------ */
/* Normalizers: the upstream API returns bare arrays with its own field
/* names. Everything below maps them onto the shapes the UI expects.  */
/* ------------------------------------------------------------------ */

/** Upstream sends numbers as raw numbers OR numeric strings ("0.9900"). */
const num = (v: unknown): number | undefined => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
};
const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.length > 0 ? v : undefined;

/** Normalise a price to a 0–1 probability (Kalshi sometimes quotes cents). */
const prob = (v: unknown): number | undefined => {
  const n = num(v);
  if (n == null) return undefined;
  if (n > 1 && n <= 100) return n / 100;
  if (n > 100) return undefined;
  return n;
};

const strArray = (v: unknown): string[] | undefined =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : undefined;

/** Accept a bare array, or a wrapped object under any of `keys`. */
function toArray(data: unknown, ...keys: string[]): Row[] {
  if (Array.isArray(data)) return data as Row[];
  if (data && typeof data === 'object') {
    for (const k of [...keys, 'data', 'results', 'items']) {
      const v = (data as Row)[k];
      if (Array.isArray(v)) return v as Row[];
    }
  }
  return [];
}

function normalizeEvent(r: Row): OddpoolEvent {
  return {
    event_id: String(r.event_id ?? r.id ?? r.ticker ?? ''),
    venue: str(r.exchange) ?? str(r.venue) ?? 'unknown',
    title: str(r.title) ?? str(r.event_title) ?? str(r.question) ?? str(r.name) ?? 'Untitled event',
    series_id: str(r.series_id) ?? str(r.series_ticker),
    end_date: str(r.close_time) ?? str(r.end_date) ?? str(r.end_time) ?? str(r.expiration_time),
    volume: num(r.total_volume) ?? num(r.volume),
    liquidity: num(r.total_liquidity) ?? num(r.liquidity),
    market_count: num(r.market_count) ?? num(r.num_markets),
    category: str(r.category),
    status: str(r.status),
    image_url: str(r.image_url) ?? str(r.image) ?? str(r.icon),
    market_questions: strArray(r.market_questions),
    discovered_at: str(r.discovered_at),
  };
}

function tokenIds(r: Row): { yes?: string; no?: string } {
  const raw = r.clob_token_ids ?? r.token_ids ?? r.tokens;
  let list: unknown[] = [];
  if (Array.isArray(raw)) list = raw;
  else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) list = parsed;
    } catch {
      /* not JSON */
    }
  }
  const ids = list
    .map((t) => (typeof t === 'string' ? t : (t as Row)?.token_id))
    .filter((t): t is string => typeof t === 'string' && t.length > 0);
  return {
    yes: str(r.yes_token_id) ?? ids[0],
    no: str(r.no_token_id) ?? ids[1],
  };
}

function normalizeMarket(r: Row): OddpoolMarket {
  const yes = prob(r.last_yes_price) ?? prob(r.yes_price) ?? prob(r.price);
  const noRaw = prob(r.last_no_price) ?? prob(r.no_price);
  const no = noRaw ?? (yes != null ? 1 - yes : undefined);
  const tokens = tokenIds(r);
  return {
    market_id: String(r.market_id ?? r.id ?? r.ticker ?? ''),
    event_id: str(r.event_id) ?? str(r.event_ticker),
    venue: str(r.exchange) ?? str(r.venue) ?? 'unknown',
    title: str(r.event_title) ?? str(r.title),
    question: str(r.question) ?? str(r.title) ?? str(r.outcome),
    outcome: str(r.outcome) ?? str(r.outcome_name),
    price: yes,
    yes_price: yes,
    no_price: no,
    volume: num(r.volume) ?? num(r.total_volume),
    liquidity: num(r.liquidity) ?? num(r.open_interest),
    end_date: str(r.close_time) ?? str(r.end_date),
    settled_at: str(r.settled_at),
    series_id: str(r.series_id) ?? str(r.series_ticker),
    yes_token_id: tokens.yes,
    no_token_id: tokens.no,
  };

}

/** Returns null when the bar carries no usable close price. */
function normalizeBar(r: Row): OhlcvBar | null {
  const close = prob(r.close) ?? prob(r.c) ?? prob(r.price);
  if (close == null) return null;
  return {
    ts: String(r.ts ?? r.timestamp ?? r.time ?? r.t ?? r.bucket ?? ''),
    open: prob(r.open) ?? prob(r.o) ?? close,
    high: prob(r.high) ?? prob(r.h) ?? close,
    low: prob(r.low) ?? prob(r.l) ?? close,
    close,
    volume: num(r.volume) ?? num(r.v) ?? 0,
  };
}

const cleanBars = (rows: Row[]): OhlcvBar[] =>
  rows
    .map(normalizeBar)
    .filter((b): b is OhlcvBar => b !== null)
    .sort((a, b) => a.ts.localeCompare(b.ts));

/**
 * Turn a grouped response (`outcomes[].bars`), a `series` list, or a flat bar
 * list into labelled series. Outcomes with no priced bars are dropped, and the
 * most active outcomes come first so charts stay readable.
 */
function normalizeOhlcv(data: unknown, labelKeys: string[]): Array<{ outcome: string; bars: OhlcvBar[] }> {
  const rows = toArray(data, 'outcomes', 'series', 'bars', 'candles', 'ohlcv');
  if (!rows.length) return [];

  let out: Array<{ outcome: string; bars: OhlcvBar[] }>;

  // Grouped: each row carries its own bar list.
  if (rows.some((r) => Array.isArray(r.bars) || Array.isArray(r.candles))) {
    out = rows.map((r) => ({
      outcome: labelKeys.map((k) => str(r[k])).find(Boolean) ?? 'Yes',
      bars: cleanBars(toArray(r.bars ?? r.candles)),
    }));
  } else {
    // Flat list of bars: group by whichever label field is present.
    const groups = new Map<string, Row[]>();
    rows.forEach((r) => {
      const label = labelKeys.map((k) => str(r[k])).find(Boolean) ?? 'Yes';
      const list = groups.get(label) ?? [];
      list.push(r);
      groups.set(label, list);
    });
    out = Array.from(groups, ([outcome, bars]) => ({ outcome, bars: cleanBars(bars) }));
  }

  const top = out
    .filter((s) => s.bars.length > 0)
    .sort((a, b) => b.bars.length - a.bars.length)
    .slice(0, 6);

  // Dedupe labels (Kalshi repeats the same question for every strike).
  const seen = new Map<string, number>();
  return top.map((s) => {
    const n = (seen.get(s.outcome) ?? 0) + 1;
    seen.set(s.outcome, n);
    return n > 1 ? { ...s, outcome: `${s.outcome} (${n})` } : s;
  });
}



function normalizeTrade(r: Row): WhaleTrade {
  const size = num(r.size) ?? num(r.shares) ?? num(r.quantity);
  const price = num(r.price) ?? num(r.avg_price);
  return {
    trade_id: str(r.trade_id) ?? str(r.id),
    event_id: str(r.event_id) ?? str(r.event_ticker),
    market_id: str(r.market_id) ?? str(r.ticker),
    venue: str(r.exchange) ?? str(r.venue),
    side: (str(r.side) ?? str(r.taker_side))?.toLowerCase() === 'sell' ? 'sell' : 'buy',
    outcome: str(r.outcome) ?? str(r.outcome_name),
    size,
    price,
    notional: num(r.notional) ?? num(r.usd_size) ?? (size != null && price != null ? size * price : undefined),
    ts: str(r.ts) ?? str(r.timestamp) ?? str(r.time),
    trader: str(r.trader) ?? str(r.wallet) ?? str(r.address),
  };
}

/** Topics used to build the default landing feed when browsing (no search). */
const BROWSE_TOPICS = ['bitcoin', 'ethereum', 'crypto', 'fed', 'election', 'ai'];

export const oddpoolService = {
  // ---- Search ----
  /**
   * Landing feed. Prefers the server-side multi-topic fan-out; if that mode is
   * not available yet it falls back to a paced client-side fan-out so the page
   * is never empty (the upstream has no "list all events" endpoint).
   */
  async browseEvents(limit = 25): Promise<{ events: OddpoolEvent[] }> {
    try {
      const raw = await invoke<unknown>('oddpool-search', { kind: 'browse', limit });
      const events = toArray(raw, 'events').map(normalizeEvent).filter((e) => e.event_id);
      if (events.length) {
        return { events: events.sort((a, b) => (b.volume ?? 0) - (a.volume ?? 0)) };
      }
    } catch {
      /* browse mode not deployed yet, fall through to the client fan-out */
    }


    const byId = new Map<string, OddpoolEvent>();
    for (const topic of BROWSE_TOPICS) {
      try {
        const res = await this.searchEvents(topic, limit);
        res.events.forEach((e) => {
          if (!byId.has(e.event_id)) byId.set(e.event_id, e);
        });
      } catch {
        /* skip a failing topic (rate limit / plan) */
      }
      await new Promise((r) => setTimeout(r, 150));
    }
    return {
      events: Array.from(byId.values()).sort((a, b) => (b.volume ?? 0) - (a.volume ?? 0)),
    };
  },
  async searchEvents(query?: string, limit = 50): Promise<{ events: OddpoolEvent[] }> {
    const raw = await invoke<unknown>('oddpool-search', {
      kind: query ? 'events' : 'recent_events', query, limit,
    });
    const seen = new Set<string>();
    const events = toArray(raw, 'events')
      .map(normalizeEvent)
      .filter((e) => e.event_id && !seen.has(e.event_id) && seen.add(e.event_id));
    return { events };
  },
  async searchMarkets(query?: string, limit = 50): Promise<{ markets: OddpoolMarket[] }> {
    const raw = await invoke<unknown>('oddpool-search', {
      kind: query ? 'markets' : 'recent_markets', query, limit,
    });
    return { markets: toArray(raw, 'markets').map(normalizeMarket).filter((m) => m.market_id) };
  },
  async searchSeries(query?: string, limit = 50) {
    const raw = await invoke<unknown>('oddpool-search', { kind: 'series', query, limit });
    return {
      series: toArray(raw, 'series').map((r) => ({
        series_id: String(r.series_id ?? r.ticker ?? r.id ?? ''),
        title: str(r.title) ?? str(r.name),
        venue: str(r.exchange) ?? str(r.venue) ?? 'unknown',
      })),
    };
  },
  async eventMarkets(event_id: string): Promise<{ markets: OddpoolMarket[] }> {
    const raw = await invoke<unknown>('oddpool-search', { kind: 'event_markets', event_id });
    return { markets: toArray(raw, 'markets').map(normalizeMarket).filter((m) => m.market_id) };
  },

  // ---- Arbitrage (plan-gated) ----
  async arbitrageCurrent(min_net_cents = 0.5): Promise<{ opportunities: ArbOpportunity[] }> {
    const raw = await invoke<unknown>('oddpool-arbitrage', { mode: 'current', min_net_cents });
    return { opportunities: toArray(raw, 'opportunities') as ArbOpportunity[] };
  },
  async arbitrageSpreads(): Promise<{ differences: ArbOpportunity[] }> {
    const raw = await invoke<unknown>('oddpool-arbitrage', { mode: 'difference' });
    return { differences: toArray(raw, 'differences') as ArbOpportunity[] };
  },

  // ---- Whales (plan-gated) ----
  async whaleFeed(limit = 100): Promise<{ trades: WhaleTrade[] }> {
    const raw = await invoke<unknown>('oddpool-whales', { kind: 'feed', limit });
    return { trades: toArray(raw, 'trades').map(normalizeTrade) };
  },
  whaleStats() {
    return invoke<Record<string, unknown>>('oddpool-whales', { kind: 'stats' });
  },
  async whaleEvents() {
    const raw = await invoke<unknown>('oddpool-whales', { kind: 'events' });
    return { events: toArray(raw, 'events') };
  },
  async whaleEventTrades(event_id: string, limit = 100): Promise<{ trades: WhaleTrade[] }> {
    const raw = await invoke<unknown>('oddpool-whales', { kind: 'event_trades', event_id, limit });
    return { trades: toArray(raw, 'trades').map(normalizeTrade) };
  },
  whaleEventStats(event_id: string) {
    return invoke<Record<string, unknown>>('oddpool-whales', { kind: 'event_stats', event_id });
  },
  async whaleMarketTrades(market_id: string, limit = 100): Promise<{ trades: WhaleTrade[] }> {
    const raw = await invoke<unknown>('oddpool-whales', { kind: 'market_trades', market_id, limit });
    return { trades: toArray(raw, 'trades').map(normalizeTrade) };
  },
  whaleMarketStats(market_id: string) {
    return invoke<Record<string, unknown>>('oddpool-whales', { kind: 'market_stats', market_id });
  },

  // ---- OHLCV ----
  /**
   * Event-level history. Very large events (hundreds of strikes) can time out
   * upstream, so we fall back to fetching history for a handful of markets.
   */
  async eventOhlcv(
    event_id: string,
    interval: '6h' | '1d' | '1w' | '1m' = '1d',
    fallbackMarketIds: string[] = [],
  ) {
    const labels = ['outcome', 'outcome_name', 'outcome_label', 'question', 'market_id', 'ticker'];
    try {
      const raw = await invoke<unknown>('oddpool-ohlcv', { kind: 'event', event_id, interval });
      const series = normalizeOhlcv(raw, labels);
      if (series.length) return { series };
    } catch {
      /* fall through to per-market history */
    }
    if (fallbackMarketIds.length) {
      try {
        return await this.marketOhlcv(fallbackMarketIds.slice(0, 6), interval);
      } catch {
        /* no history available */
      }
    }
    return { series: [] as Array<{ outcome: string; bars: OhlcvBar[] }> };
  },

  async marketOhlcv(market_ids: string[], interval: '6h' | '1d' | '1w' | '1m' = '1d') {
    const raw = await invoke<unknown>('oddpool-ohlcv', {
      kind: 'market', market_ids: market_ids.join(','), interval,
    });
    return { series: normalizeOhlcv(raw, ['market_id', 'ticker', 'outcome']) };
  },

  // ---- Historical (Kalshi / Polymarket) ----
  /** Upstream caps `limit` at 200. */
  kalshiHistory(market_id: string, kind: 'top_of_book' | 'orderbook' | 'trades' = 'top_of_book', extra: Record<string, unknown> = {}) {
    const limit = Math.min(Number(extra.limit ?? 200), 200);
    return invoke<Record<string, unknown>>('oddpool-historical-kalshi', { market_id, kind, ...extra, limit });
  },
  polymarketHistory(market_id: string, kind: 'top_of_book' | 'orderbook' | 'trades' = 'top_of_book', extra: Record<string, unknown> = {}) {
    const limit = Math.min(Number(extra.limit ?? 200), 200);
    return invoke<Record<string, unknown>>('oddpool-historical-polymarket', { market_id, kind, ...extra, limit });
  },

  // ---- Feed catalog ----
  async feedCatalog(kind: 'macro' | 'crypto' = 'macro') {
    const raw = await invoke<unknown>('oddpool-feed-catalog', { kind });
    return { feeds: toArray(raw, 'feeds', 'channels', 'events') };
  },


  // ---- WebSocket relay ----
  /** Start a relay session; client should also subscribe to `oddpool:<channel>` via Supabase Realtime. */
  startRelay(channels: string[]) {
    return invoke<{ ok: boolean }>('oddpool-ws-relay', { channels });
  },
  subscribeChannel(channel: string, onMessage: (msg: unknown) => void) {
    const ch = supabase.channel(`oddpool:${channel}`);
    ch.on('broadcast', { event: 'message' }, (p) => onMessage(p.payload)).subscribe();
    return () => { supabase.removeChannel(ch); };
  },

  // ---- AI agents ----
  aiAnalyzeEvent(event_id: string, title?: string) {
    return invoke<{ analysis: AiAnalysis }>('ai-market-analyst', { event_id, title });
  },
  aiExplainArbitrage(opportunity: ArbOpportunity) {
    return invoke<{ explanation: Record<string, unknown> }>('ai-arbitrage-explainer', { opportunity });
  },
  aiInterpretWhales(event_id?: string, limit = 25) {
    return invoke<{ insights: Array<Record<string, unknown>> }>('ai-whale-interpreter', { event_id, limit });
  },
  aiCopilot(messages: Array<{ role: string; content: string }>) {
    return invoke<{ reply: string }>('ai-research-copilot', { messages });
  },
  aiDailyDigest() {
    return invoke<{ digest: Record<string, unknown>; digest_date: string }>('ai-daily-digest', {});
  },

  // ---- Tracked events ----
  async trackEvent(userId: string, event_id: string, venue: Venue, title?: string) {
    const { error } = await supabase
      .from('oddpool_tracked_events')
      .insert({ user_id: userId, event_id, venue, title: title || null });
    if (error) throw error;
  },
  async untrackEvent(userId: string, event_id: string) {
    const { error } = await supabase
      .from('oddpool_tracked_events')
      .delete()
      .eq('user_id', userId)
      .eq('event_id', event_id);
    if (error) throw error;
  },
  async getTrackedEventIds(userId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('oddpool_tracked_events')
      .select('event_id')
      .eq('user_id', userId);
    if (error) throw error;
    return (data ?? []).map((r) => r.event_id);
  },

  // ---- Whale alerts ----
  async getWhaleAlerts(userId: string) {
    const { data, error } = await supabase
      .from('oddpool_whale_alerts')
      .select('*')
      .eq('user_id', userId);
    if (error) throw error;
    return data ?? [];
  },
  async upsertWhaleAlert(userId: string, event_id: string, threshold_usd: number, venue?: Venue) {
    const { error } = await supabase
      .from('oddpool_whale_alerts')
      .upsert({ user_id: userId, event_id, threshold_usd, venue: venue ?? null, enabled: true } as never, {
        onConflict: 'user_id,event_id',
      });
    if (error) throw error;
  },
  async deleteWhaleAlert(userId: string, event_id: string) {
    const { error } = await supabase
      .from('oddpool_whale_alerts')
      .delete()
      .eq('user_id', userId)
      .eq('event_id', event_id);
    if (error) throw error;
  },
};
